/**
 * Centralized dialog messages - single source of truth for all dialog text.
 *
 * This file contains all user-facing text used in dialogs throughout the app.
 * When you need to change a confirmation message or warning, change it here.
 *
 * @example
 * import { dialogMessages } from '@/constants';
 * <Text>{dialogMessages.delete.confirm('contact')}</Text>
 */

/**
 * Helper to pluralize entity names
 */
function pluralize(word: string, count: number): string {
  return count === 1 ? word : `${word}s`;
}

/**
 * When a refused action can be tried again, as the end of a sentence (#1163):
 * "in 1 minute" / "in N minutes" (N = seconds / 60, rounded up, never below 1),
 * or "later" when the server did not say.
 */
export function retryInPhrase(seconds: number | null): string {
  if (seconds === null) return 'later';
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  return `in ${minutes} ${pluralize('minute', minutes)}`;
}

/** What a list's rows are called to the user (Q-490: packets are courses). */
export type BulkListEntity = 'packet' | 'checklist' | 'contact';
const bulkListNouns: Record<BulkListEntity, string> = {
  packet: 'course',
  checklist: 'checklist',
  contact: 'contact',
};

/** The plural noun for a list's rows (Q-490): "courses", "checklists", "contacts". */
export function bulkListNoun(entityType: BulkListEntity): string {
  return pluralize(bulkListNouns[entityType], 2);
}

export const dialogMessages = {
  /**
   * Account-merge dialog (AccountMergeDialog — proactive login/refresh
   * variant and the 403 denial variant). Forced binary copy: no soft
   * "maybe later" phrasing.
   */
  accountMerge: {
    /** Dialog title (both variants) */
    title: 'Merge Accounts',

    /** Proactive variant intro (login / auth-me refresh). */
    proactiveIntro: (methodCount: number, methodType: 'email' | 'phone') =>
      methodCount > 1
        ? 'You may have another Ving account. Select a contact method below to verify ownership and merge accounts, or choose Ignore if it isn’t yours:'
        : `You may have another Ving account. Select the ${methodType} below to verify ownership and merge accounts, or choose Ignore if it isn’t yours:`,

    /**
     * Denial (403) variant intro — explicit that another account of theirs
     * may hold the denied access, naming the account and the grant.
     */
    denialIntro: (displayName: string, level: string, contextName: string | null) =>
      `You don’t have access to this, but ${displayName} — an account that may be yours — has ${level} access ${
        contextName ? `in ${contextName}` : 'here'
      }. Verify a contact method below to merge accounts, or go back:`,

    /**
     * Denial variant, CONTENT grant — the consume gate. The other account
     * holds no seat to inherit; what it holds is the org's assignment of this
     * exact share. So the copy names the content, not a level or a context.
     * Says nothing about which method or contact is involved: the person has
     * proven nothing yet at this point.
     */
    denialContentIntro: (displayName: string) =>
      `This was assigned to ${displayName} — an account that may be yours. Verify a contact method below to merge accounts and open it, or go back:`,

    /** Verify-step body */
    codeSent: (channel: 'email' | 'SMS') =>
      `A verification code was sent via ${channel}. Please enter it below:`,

    /**
     * Verify-step body after a refused send (#1163 addendum, PM): claims
     * nothing, since no code may have been sent this hour.
     */
    codePrompt: 'Enter your verification code:',

    /** Buttons */
    ignore: 'Ignore',
    goBack: 'Go Back',
    verify: 'Verify',
    cancel: 'Cancel',

    /** Feedback */
    mergeSuccess: 'Your accounts were merged successfully.',
    verifyFailed: 'Verification failed. Please try again.',
    sendFailed: 'Failed to send verification code',
    /**
     * #1163 (vingapi#1666 S2): the send budget is spent. Nothing was sent, but
     * a code an earlier press sent still works, so the code step stays open.
     * `when` is `retryInPhrase(...)`.
     */
    sendRateLimited: (when: string) =>
      `No new code was sent: too many codes were requested. If you already have a code, enter it below. You can request another ${when}.`,
    ignoreSuccess: 'This will not be suggested again.',
    ignoreFailed: 'Failed to dismiss suggestion',

    /**
     * #223 defect E, in copy: the code goes to the OTHER account's verified
     * method, which for a duplicate made years ago can be a number the person
     * no longer has. Without this line the dialog is a dead end they can only
     * ignore — and ignoring is what put them there. Staff are the resolver.
     */
    cannotReceiveCodePrefix: 'I can’t receive this code —',
    contactSupport: 'contact support',
  },

  /**
   * Cross-user contact-method claim (identity-plan.md §7). Adding a method
   * another account has VERIFIED is the start of a user merge, not a dead
   * end — one contact method identifies one person (D1).
   *
   * NOTHING here may hint at whose account it is. The claimant has proven
   * nothing yet, and listing a suggestion would expose the counterpart's
   * entire verified method list (`get_verify_methods_for_user`), making a
   * typed guess into an enumeration vector. "Another account" is the most we
   * ever say — matching vingapi's own deliberately opaque wording.
   */
  contactMethodClaim: {
    title: 'Merge Accounts',

    /**
     * Unlike the merge dialog's fork, there is no method to choose: the code
     * goes to the address the user just typed. Restricting it to what they
     * demonstrably already knew is precisely what stops the 409 leaking
     * anything new.
     */
    intro: (method: string) =>
      `${method} is already verified on another account. Since a contact method belongs to one person, verifying it here will merge that account into yours.`,

    codeSent: (method: string) =>
      `A verification code was sent to ${method}. Enter it below to merge the accounts:`,

    claim: 'Send Code',
    verify: 'Verify & Merge',
    cancel: 'Cancel',

    /** Feedback */
    mergeSuccess: 'Your accounts were merged successfully.',
    verifyFailed: 'Verification failed. Please try again.',
    claimFailed: 'Could not start the merge. Please try again.',
    /**
     * §7's state transition: the owner removed the method while the claim was
     * pending, so it is unowned now and an ordinary add just works. Not an
     * error the user has to understand — just point them back at the add.
     */
    notClaimable: 'This is no longer in use by another account — try adding it again.',
    /** `when` is `retryInPhrase(...)`: the claim's `retry_after`, else "later" (#1163). */
    rateLimited: (when: string) => `Too many attempts. Please try again ${when}.`,
  },

  /**
   * Delete confirmation messages
   */
  delete: {
    /** Dialog title */
    title: (entityType: string) => `Delete ${entityType}`,

    /** Dialog title for multiple items */
    titleMultiple: (entityType: string) => `Delete ${entityType}s`,

    /** Main confirmation question */
    confirm: (entityType: string) =>
      `Are you sure you want to permanently delete this ${entityType}?`,

    /** Confirmation with item name */
    confirmNamed: (itemName: string) =>
      `Are you sure you want to permanently delete ${itemName}?`,

    /** Confirmation for multiple items */
    confirmMultiple: (entityType: string, count: number) =>
      `Are you sure you want to permanently delete ${count} ${pluralize(entityType, count)}?`,

    // Q-571: the editors' compact delete confirms (#1756), one sentence each.
    /** Course quiz question and checklist question */
    questionCompact: 'Delete this question?',
    /** Course component */
    componentCompact: 'Delete this component?',

    /** Standard warning about irreversibility */
    warning: 'This action cannot be undone.',

    /** Intro text before listing what will be lost */
    dataLossIntro: 'The following data will be permanently lost:',

    /** Type-to-confirm instruction */
    typeToConfirm: (text: string) => `Type "${text}" to confirm deletion.`,

    /** Case-insensitive type-to-confirm hint */
    typeToConfirmHint: 'Confirmation is case-insensitive.',
  },

  /**
   * Archive confirmation messages
   */
  archive: {
    /** Dialog title */
    title: (entityType: string) => `Archive ${entityType}`,

    /** Main confirmation question */
    confirm: (entityType: string) =>
      `Are you sure you want to archive this ${entityType}?`,

    /** Confirmation with item name */
    confirmNamed: (itemName: string) =>
      `Are you sure you want to archive ${itemName}?`,

    /** Confirmation for multiple items */
    confirmMultiple: (entityType: string, count: number) =>
      `Are you sure you want to archive ${count} ${pluralize(entityType, count)}?`,

    /** Q-506 card 1545 + Q-530 a: compact list confirm, n = the SELECTED count. */
    bulkPrompt: (entityType: BulkListEntity, count: number) =>
      `Archive ${count} ${pluralize(bulkListNouns[entityType], count)}?`,

    /** Warning about active assignments */
    activeAssignments: (count: number, entityType: string) =>
      `This ${entityType} has ${count} active ${pluralize('assignment', count)}. ` +
      `Archiving will remove it from active views but preserve the data.`,

    /** Warning about pending submissions */
    pendingSubmissions: (count: number, entityType: string) =>
      `This ${entityType} has ${count} pending ${pluralize('submission', count)}. ` +
      `These will still be accessible but no new submissions can be made.`,

    /** Explanation of archive behavior */
    explanation: (entityType: string) =>
      `Archived ${entityType}s will no longer appear in the main list but can be restored later.`,

    /** Success message */
    success: (entityType: string) => `${entityType} archived successfully.`,
  },

  /**
   * Unarchive/restore confirmation messages
   */
  unarchive: {
    /** Dialog title */
    title: (entityType: string) => `Restore ${entityType}`,

    /** Main confirmation question */
    confirm: (entityType: string) =>
      `Are you sure you want to restore this ${entityType}?`,

    /** Confirmation with item name */
    confirmNamed: (itemName: string) =>
      `Are you sure you want to restore ${itemName}?`,

    /** Q-506 card 1545 + Q-530 a: compact list confirm, n = the SELECTED count. */
    bulkPrompt: (entityType: BulkListEntity, count: number) =>
      `Unarchive ${count} ${pluralize(bulkListNouns[entityType], count)}?`,

    /** Confirmation for multiple items */
    confirmMultiple: (entityType: string, count: number) =>
      `Are you sure you want to restore ${count} ${pluralize(entityType, count)}?`,

    /** Explanation of restore behavior */
    explanation: (entityType: string) =>
      `This ${entityType} will be restored to the main list.`,

    /** Success message */
    success: (entityType: string) => `${entityType} restored successfully.`,
  },

  /**
   * Share dialog messages
   */
  share: {
    /** Accessible (sr-only) dialog title (#840): names the content when known. */
    title: (contentTitle?: string) => (contentTitle?.trim() ? `Share ${contentTitle.trim()}` : 'Share'),

    /** Visible title and accessible name when a selection is being reminded (Q-506 card 1547; Q-521 a). */
    remindTitle: 'Remind',

    /** Reveal on a muted recipient chip the send reaches by no method (Q-506 card 1547). */
    recipientUnreachable: 'No email or phone',

    /** Reveal on a muted recipient chip Remind leaves out (Q-506 card 1547 a, PM 10-14). */
    recipientCompleted: 'Completed',

    /** Validation error when no recipients */
    noRecipients: 'Please select at least one recipient.',

    /** Success message after sending */
    sendSuccess: (count: number) =>
      `Successfully sent to ${count} ${pluralize('recipient', count)}.`,

    /** Success message after scheduling */
    scheduleSuccess: (count: number) =>
      `Successfully scheduled for ${count} ${pluralize('recipient', count)}.`,

    /** Prompt to add recipients */
    addRecipientsPrompt: 'Add contacts, emails, or phone numbers to share with.',
  },

  /**
   * Bulk share dialog messages
   */
  bulkShare: {
    /** Dialog title */
    title: 'Assign and Send Invitations',

    /** Success message after scheduling */
    scheduleSuccess: (count: number) =>
      `Successfully scheduled ${count} ${pluralize('invitation', count)}.`,

    /** Empty state when all items removed */
    emptyItems: 'No items to share. Close and select items from the list.',

    /** Scheduling section label */
    schedulingLabel: 'Set Schedule',

    /** Interval option labels */
    intervalAllAtOnce: 'All at once',
    intervalDaily: 'Daily',
    intervalWeekly: 'Weekly',
    intervalMonthly: 'Monthly',

    /** Apply button label */
    applySchedule: 'Apply',
  },

  /**
   * Export dialog messages
   */
  export: {
    /** Dialog title */
    title: 'Export Data',

    /** Description with record count */
    description: (count: number, entityType: string) =>
      `Export ${count.toLocaleString()} ${pluralize(entityType, count)} to a file.`,

    /** Format selection label */
    formatLabel: 'Export Format',

    /** Fields selection label */
    fieldsLabel: 'Fields to Export',

    /** Fields selection status */
    fieldsStatus: (selected: number, total: number) =>
      `${selected} of ${total} ${pluralize('field', total)} selected`,

    /** Validation error when no fields selected */
    noFields: 'Please select at least one field to export.',

    /** Success message */
    success: (count: number) =>
      `Successfully exported ${count} ${pluralize('record', count)}.`,

    /** Error message */
    failed: 'Export failed. Please try again.',

    /** Processing message */
    processing: 'Preparing export...',
  },

  /**
   * Merge contacts dialog messages
   */
  merge: {
    /** Dialog title */
    title: 'Merge',

    /** Main confirmation question */
    confirm: 'Are you sure you want to merge these contacts?',

    /**
     * Staff's confirmation question: they check the contact to keep
     * (#1959, owner Q-663). Customer admins keep `confirm`.
     */
    confirmStaff: 'Merge into the checked contact?',
  },

  /**
   * Unsaved changes dialog messages
   */
  unsavedChanges: {
    /** The editor's leave prompt (card 1538, Q-529 a, Q-530 a): shown only after a save has failed. */
    message: "Your last change isn't saved. Leave anyway?",

    /** Stay button text */
    stay: 'Stay',

    /** Leave button text */
    leave: 'Leave',
  },

  /** The editor's Publish asks first (Q-506 card 1537, Q-529 a, Q-530 a). */
  publish: {
    activeConfirm: 'Publish? This becomes the active version.',
  },

  /**
   * Generic confirmation messages
   */
  confirm: {
    /** Generic confirmation title */
    title: 'Confirm Action',

    /** Generic confirmation question */
    message: 'Are you sure you want to proceed?',
  },

  /**
   * Error dialog messages
   */
  error: {
    /** Dialog title */
    title: 'Error',

    /** Generic error message */
    generic: 'An error occurred. Please try again.',

    /** Network error */
    network: 'Unable to connect. Please check your internet connection.',

    /** Permission error */
    permission: 'You do not have permission to perform this action.',

    /** Trace ID label */
    traceIdLabel: 'Trace ID',

    /** Failure screen — shown for render crashes and 5xx (the "we don't know what happened" class). */
    failureMessage: 'An error was encountered and escalated for investigation.',
    failureTryAgain: 'Try again',
    failureDashboard: 'Back to dashboard',
  },

  /**
   * Info/acknowledgment dialog messages
   */
  info: {
    /** Dialog title */
    title: 'Information',
  },

  /**
   * Password reset dialog messages
   */
  passwordReset: {
    /** Dialog title */
    title: 'Reset Password',

    /** Success message */
    success: 'Password successfully updated.',

    /** Error message */
    failed: 'Failed to update password. Please try again.',

    /** Field labels (Q-506 card 1562: "Password", reworded from "New Password") */
    newPasswordLabel: 'Password',
    confirmPasswordLabel: 'Confirm Password',

    /** Footer buttons (Q-506 card 1562: new/reworded; not the shared Update / Not Now labels) */
    setPasswordButton: 'Set Password',
    notNowButton: 'Not now',

    /** Validation errors */
    repeatedChars: 'Password cannot have 4 or more repeated characters',
    sequentialChars: 'Password cannot have 4 or more sequential characters',
  },

  /**
   * Password prompt dialog — shown on sign-in when the account needs a
   * password (admin-created / provisioned accounts OR reset-link arrivals,
   * #529). The client cannot distinguish the two cases, so the copy must
   * be true for both. Dismissable: the person can skip and continue.
   */
  passwordPrompt: {
    /** Dialog title */
    title: 'Set a Password',
  },

  /**
   * Change owner dialog messages
   */
  changeOwner: {
    /** Dialog title */
    title: 'Change Owner',

    /** Description */
    description: (entityType: string, count: number) =>
      `Select a new owner for ${count} ${pluralize(entityType, count)}.`,

    /** Current owner label */
    currentOwner: 'Current Owner',

    /** Search placeholder */
    searchPlaceholder: 'Search users...',

    /** No results */
    noUsersFound: 'No users found',

    /** No available users */
    noAvailableUsers: 'No available users',

    /** Confirmation message */
    confirm: (entityType: string) =>
      `Are you sure you want to change the owner of this ${entityType}?`,

    /** Warning about access */
    warning: 'The new owner will have full control over this item.',

    /** Success message */
    success: 'Owner changed successfully.',
  },

  /**
   * Assign labels dialog messages
   */
  assignLabels: {
    /** Dialog title for single item */
    title: 'Assign Labels',

    /** Dialog title - add mode */
    titleAdd: 'Add Labels',

    /** Dialog title for multiple items */
    titleMultiple: (count: number) => `Assign Labels to ${count} Items`,

    /** No labels selected */
    noLabels: 'No labels selected.',

    /** No labels available */
    noLabelsAvailable: 'No labels available.',

    /** Select labels prompt */
    selectPrompt: (entityType: string, count: number) =>
      count > 1
        ? `Select labels to add to ${count} ${pluralize(entityType, count)}.`
        : `Select labels to add to this ${entityType}.`,

    /** Replace mode warning */
    replaceWarning: 'This will replace existing labels.',

    /** Success message */
    success: (count: number) =>
      `Labels assigned to ${count} ${pluralize('item', count)}.`,
  },

  /**
   * Manage labels dialog messages
   */
  manageLabels: {
    /** Dialog title */
    title: 'Manage Labels',

    /** Search placeholder */
    searchPlaceholder: 'Search or add new label...',

    /** Empty state — the entity type has no labels at all yet. */
    noLabelsSelected: 'No labels yet. Type above to create one.',

    /** Success message */
    success: 'Labels updated successfully.',

    /** Legend explaining the tri-state chip styling. */
    legend: {
      prefix: 'Labels applied to',
      all: 'All',
      some: 'Some',
      none: 'None',
    },

    /** Create-a-new-label affordance shown while searching an unused name. */
    createNew: (name: string) => `Create "${name}"`,

    /** Hint shown when unapplied labels are capped on first open. */
    moreHidden: (count: number) => `+${count} more — type to filter`,
  },

  /**
   * Manage Attributes dialog messages
   */
  manageAttributes: {
    /** Dialog title */
    title: 'Set Attributes',

    /** No attribute dimensions configured */
    noAttributes: 'No attribute dimensions configured.',
  },

  /**
   * Upload dialog messages
   */
  upload: {
    /** Dialog title */
    title: 'Upload File',

    /** Drag and drop prompt */
    dragDrop: 'Drag and drop a file here, or click to select',

    /** Drop here prompt */
    dropHere: 'Drop file here',

    /** Browse prompt */
    browse: 'Browse',

    /** Upload complete */
    complete: 'Upload Complete',

    /** Max size info */
    maxSize: (sizeMb: number) => `Max size: ${sizeMb}MB`,

    /** Accepted formats info */
    acceptedFormats: (formats: string) => `Accepted formats: ${formats}`,

    /** Progress display */
    progress: (percent: number) => `Uploading... ${percent}%`,

    /** Error messages */
    invalidType: 'Invalid file type.',
    fileTooLarge: (maxMb: number) => `File size must be less than ${maxMb}MB.`,
    uploadFailed: 'Upload failed. Please try again.',
  },

  /**
   * Image upload/crop dialog messages
   */
  imageCrop: {
    /** Dialog title */
    title: 'Upload Image',

    /** Drop prompt */
    dropHere: 'Drop Image Here',

    /** Accepted formats */
    acceptedFormats: 'Accepted formats: JPEG, PNG, GIF, WebP',
  },

  /**
   * Image picker dialog messages (generic image upload)
   */
  imagePicker: {
    /** Dialog title */
    title: 'Select Image',

    /** Title variants */
    titleQuestion: 'Question Image',
    titleChoice: 'Choice Image',

    /** Drop zone prompt */
    dropPrompt: 'Drag and drop an image here, or click to browse',

    /** Browse button */
    browse: 'Browse Files',

    /** Current image label */
    currentImage: 'Current Image',

    /** Remove image confirmation */
    removeConfirm: 'Remove this image?',

    /** Accepted formats */
    acceptedFormats: 'JPEG, PNG, GIF, WebP',

    /** Max size */
    maxSize: (sizeMb: number) => `Max file size: ${sizeMb}MB`,

    /** Error messages */
    invalidType: 'Please select a valid image file (JPEG, PNG, GIF, or WebP).',
    fileTooLarge: (maxMb: number) => `Image must be smaller than ${maxMb}MB.`,
    uploadFailed: 'Failed to upload image. Please try again.',
  },

  /**
   * Copy link dialog messages
   */
  copyLink: {
    /** Accessible (sr-only) dialog title (#840). */
    title: 'Copy link',
  },

  /**
   * QR code dialog messages
   */
  qrCode: {
    /** Accessible (sr-only) dialog title (#840): names the item when it has a title. */
    title: (itemTitle?: string) => (itemTitle?.trim() ? `QR code for ${itemTitle.trim()}` : 'QR code'),
    /** No QR code generated yet (it is generated after publish). Never for a failed fetch. */
    noQrCode: 'No QR code available',
    /** Fetching the QR code failed (#407) — the QR may well exist. */
    loadFailed: "Couldn't load the QR code. Try again.",
  },

  /**
   * Resolve dialog messages
   */
  resolve: {
    /** Dialog title */
    title: 'Resolve Flagged Items',

    /** Confirmation for submissions */
    confirmSubmissions: (count: number) =>
      `This will resolve all flagged responses for the selected ${pluralize('submission', count)}. Are you sure you want to continue?`,

    /** Confirmation for responses */
    confirmResponses: (count: number) =>
      `This will resolve the selected flagged ${pluralize('response', count)}. Are you sure you want to continue?`,

    /** Count message */
    countMessage: (count: number, isResponses: boolean) =>
      `${count} ${isResponses ? pluralize('response', count) : pluralize('submission', count)} will be resolved.`,

    /** Items to resolve label */
    itemsToResolve: 'Items to resolve',

    /** No flagged items */
    noFlaggedItems: (isResponses: boolean) =>
      `No flagged items selected to resolve. Select flagged ${isResponses ? 'responses' : 'submissions'} first.`,

    /** Error message */
    error: 'There was an error while resolving. Please try again.',
  },

  /**
   * Recipient picker dialog messages
   */
  recipientPicker: {
    /** Search placeholder */
    searchPlaceholder: 'Search contacts, labels, or enter email/phone...',

    /** No results */
    noResults: 'No results found',

    /** Section headers */
    labels: 'Labels',
    filters: 'Filters',
    attributes: 'Attributes',
    contacts: 'Contacts',
    newEmails: 'New Emails',
    newPhones: 'New Phones',

    /** Contact count */
    contactCount: (count: number) => `${count} ${pluralize('contact', count)}`,

    /** Add prompt */
    addAsEmail: (value: string) => `Add "${value}" as email`,
    addAsPhone: (value: string) => `Add "${value}" as phone`,
    addItem: (value: string) => `Add "${value}"`,
  },

  /**
   * Message viewer dialog messages
   */
  messageViewer: {
    /** Assignment label */
    assignmentLabel: 'Assignment',

    /** Error loading */
    loadFailed: 'Failed to load messages',

    /** Empty state */
    noMessages: 'No messages yet',

    /** Title with unread count */
    titleWithUnread: (unreadCount: number) => `Messages (${unreadCount} new)`,

    /** Title without unread */
    title: 'Messages',
  },

  /**
   * Settings dialog messages
   */
  settings: {
    /** Dialog title */
    title: 'Settings',
  },

  /**
   * Organization/Network settings dialog (ContextSettingsDialog) messages
   */
  contextSettings: {
    /** A logo, header logo or badge logo removal failed (#845) */
    logoRemoveFailed: 'Failed to remove logo. Please try again.',
    /** A logo, header logo or badge logo save failed for a reason other than a file decline (#885) */
    logoSaveFailed: 'Failed to save logo. Please try again.',
    /** The context rename (PATCH /api/context/) failed (#871; copy unchanged) */
    renameFailed: 'Could not rename — please try again.',
  },

  /**
   * Edit Profile dialog messages
   */
  profileEdit: {
    /** The avatar's upload step (presign or S3) failed (#885 D1) */
    avatarUploadFailed: 'Failed to upload avatar. Please try again.',
  },

  /**
   * Premium content library messages
   */
  premiumLibrary: {
    /** Accessible (sr-only) dialog title (#840); the visible header is rendered in the body. */
    dialogTitle: 'Premium content library',

    /** Replace content title */
    replaceTitle: (packetTitle: string) => `Replace Content for "${packetTitle}"`,

    /** Grant title */
    grantTitle: (itemTypeName: string) => `Grant Premium ${itemTypeName}`,

    /** Library title */
    libraryTitle: (itemTypeName: string) => `Premium ${itemTypeName} Library`,

    /** Search placeholder */
    searchPlaceholder: (itemTypeName: string) => `Search premium ${itemTypeName.toLowerCase()}...`,

    /** Selected count */
    selectedCount: (count: number) => `${count} selected`,

    /** Empty state - filters */
    noMatchFilters: (itemTypeName: string) => `No ${itemTypeName.toLowerCase()} match your filters`,

    /** Empty state - no items */
    noItemsAvailable: (itemTypeName: string) => `No premium ${itemTypeName.toLowerCase()} available`,

    /** Summary section */
    summary: 'Summary',

    /** Grant description */
    grantDescription: (count: number, itemTypeName: string, orgName: string) =>
      `Granting ${count} ${itemTypeName.toLowerCase()} to ${orgName}`,

    /** Selected items toggle */
    showSelectedItems: 'Show selected items',
    hideSelectedItems: 'Hide selected items',

    /** Owner selection */
    selectOwner: 'Select Owner',
    selectOwnerDescription: 'Choose an admin or manager to own the granted items.',
    searchUsers: 'Search users...',
    noUsersMatch: 'No users match your search',
    noOrgUsers: 'No admin/manager users in this organization',

    /** Errors */
    loadUsersFailed: 'Failed to load organization users',
    replaceContentFailed: 'Failed to replace content',
    grantFailed: (errors: string) => `Failed to grant items: ${errors}`,
    operationFailed: 'Operation failed',

    /** Button labels */
    replaceContent: 'Replace Content',
    processing: 'Processing...',
    grantItems: (count: number, itemTypeSingular: string, itemTypeName: string) =>
      `Grant ${count} ${count === 1 ? itemTypeSingular : itemTypeName}`,
  },

  /**
   * Premium toggle dialog messages (staff only)
   */
  premiumToggle: {
    /** Dialog title */
    title: (entityType: string, isPremium: boolean) =>
      isPremium ? 'Remove Premium Status' : `Make ${entityType} Premium`,

    /** Confirmation message */
    confirm: (itemName: string, isPremium: boolean) =>
      isPremium
        ? `Remove premium status from "${itemName}"?`
        : `Make "${itemName}" a premium item?`,

    /** Explanation of action effect */
    explanation: (isPremium: boolean) =>
      isPremium
        ? 'This item will no longer appear in the premium content library.'
        : 'This item will appear in the premium content library for granting to organizations.',
  },

  /**
   * Staff management dialog messages
   */
  staffManagement: {
    /** Create network dialog */
    createNetwork: {
      title: 'Create Network',
      identifierHelp: 'Auto-generated from name. Lowercase letters, numbers, and hyphens only.',
    },

    /** Create organization dialog */
    createOrganization: {
      title: 'Create Organization',
      identifierHelp: 'Auto-generated from name. Lowercase letters, numbers, and hyphens only.',
    },

    /** Add organization to network dialog */
    addOrganization: {
      title: 'Add Organization to Network',
      searchPlaceholder: 'Search by name or identifier...',
      createNew: 'Create New Organization',
      seatsLabel: 'Seats (optional)',
      noResults: 'No organizations found',
    },

    /** Add user to network/org dialog */
    addUser: {
      title: (entityType: string) => `Add User to ${entityType}`,
      searchPlaceholder: 'Search by email, phone, or name...',
      provisionNew: 'Provision New User',
      provisionHelp: 'User will receive an email with a link to set their password.',
      noResults: 'No users found',
      roleLabel: 'Role',
      levelLabel: 'Level',
    },

    /** Edit seats dialog */
    editSeats: {
      title: (orgName: string) => `Edit Seats: ${orgName}`,
      seatsLabel: 'Seats',
      seatsHelp: 'Leave empty for unlimited seats.',
    },

    /** Edit user dialog */
    editUser: {
      title: (userName: string) => `Edit User: ${userName}`,
      roleLabel: 'Role',
      levelLabel: 'Level',
      logoEditorLabel: 'Can edit organization logo',
      statusLabel: 'Status',
    },

    /** Remove organization from network */
    removeOrganization: {
      title: 'Remove Organization',
      confirm: (orgName: string, networkName: string) =>
        `Are you sure you want to remove "${orgName}" from ${networkName}?`,
      warning: 'This will disconnect the organization from this network.',
    },

    /** Remove user from network */
    removeUser: {
      title: 'Remove User',
      confirm: (userName: string, entityName: string) =>
        `Are you sure you want to remove "${userName}" from ${entityName}?`,
      warning: 'The user will lose access to this network.',
    },

    /** Deactivate user in organization */
    deactivateUser: {
      title: 'Deactivate User',
      confirm: (userName: string, orgName: string) =>
        `Are you sure you want to deactivate "${userName}" in ${orgName}?`,
      explanation: 'They will lose access to this organization but can be reactivated later.',
    },

    /** Reactivate user in organization */
    reactivateUser: {
      title: 'Reactivate User',
      confirm: (userName: string, orgName: string) =>
        `Are you sure you want to reactivate "${userName}" in ${orgName}?`,
      explanation: 'They will regain access to this organization.',
    },

    /** Merge two user accounts (staff only, #223 chunk 2) */
    mergeUsers: {
      title: 'Merge User Accounts',
      searchPlaceholder: 'Search by email, phone, or name...',
      searchPrompt: 'Choose the other account to merge with this one.',
      noResults: 'No users found',
      primaryLabel: 'Keeps this account',
      duplicateLabel: 'Merged in, then permanently deleted',
      swap: 'Swap',
      movesIntro: 'Moving to the surviving account:',
      seats: (count: number) => `${count} ${pluralize('seat', count)}`,
      methods: (count: number) => `${count} contact ${pluralize('method', count)}`,
      assignments: (count: number) => `${count} ${pluralize('assignment', count)}`,
      submissions: (count: number) => `${count} ${pluralize('submission', count)}`,
      /**
       * The staff user search does not carry assignment/submission counts, so
       * the pre-merge diff cannot show them. Say so rather than implying zero.
       */
      countsUnavailable:
        'Assignment and submission counts are not available before the merge — the totals actually moved are reported when it completes.',
      confirmWord: 'MERGE',
      typeToConfirm: (text: string) => `Type "${text}" to confirm the merge.`,
      confirmButton: 'Merge Accounts',
      merging: 'Merging...',
      success: (moved: { seats: number; methods: number; assignments: number; submissions: number }) =>
        `Accounts merged. Moved ${moved.seats} ${pluralize('seat', moved.seats)}, ` +
        `${moved.methods} contact ${pluralize('method', moved.methods)}, ` +
        `${moved.assignments} ${pluralize('assignment', moved.assignments)} and ` +
        `${moved.submissions} ${pluralize('submission', moved.submissions)}.`,
      failed: 'Failed to merge the accounts. Please try again.',
    },

    /** Empty states */
    emptyState: {
      noContext: 'Select a network or organization context to manage.',
      noOrganizations: 'No organizations in this network.',
      noUsers: 'No users in this network.',
      noOrgUsers: 'No users in this organization.',
    },

    /** Success messages */
    success: {
      networkCreated: 'Network created successfully.',
      organizationCreated: 'Organization created successfully.',
      organizationAdded: 'Organization added to network.',
      organizationRemoved: 'Organization removed from network.',
      userAdded: 'User added successfully.',
      userRemoved: 'User removed successfully.',
      userDeactivated: 'User deactivated.',
      userReactivated: 'User reactivated.',
      userUpdated: 'User updated successfully.',
      seatsUpdated: 'Seats updated successfully.',
      settingsSaved: 'Settings saved.',
    },

    /** Error messages */
    error: {
      createFailed: 'Failed to create. Please try again.',
      updateFailed: 'Failed to update. Please try again.',
      removeFailed: 'Failed to remove. Please try again.',
      loadFailed: 'Failed to load data. Please refresh.',
      invalidPassingScore: 'Passing score must be a number between 0 and 1.',
    },
  },

  /**
   * Upload contacts dialog messages
   */
  uploadContacts: {
    /** Dialog title */
    title: 'Upload Contacts',

    /** Drop zone prompt */
    dropPrompt: 'Drop Import Files Here',

    /** Browse button */
    browse: 'Browse',

    /** Supported formats hint */
    supportedFormats: 'Supported formats: CSV, TSV, XLSX',

    /** Invalid file type error */
    invalidFileType: 'Invalid file type. Please upload a CSV, TSV, or Excel file.',

    /** File too large error */
    fileTooLarge: 'Invalid file selected. File size exceeds 10MB limit.',

    /** Upload failed error */
    uploadFailed: 'An error occurred while uploading. Please try again.',

    /** Success title */
    successTitle: 'Upload Started Successfully',

    /** Success message. Audit M13: this used to continue "...within your
     * History" with a link to /user/profile#history — a legacy-Angular route
     * with no SPA equivalent, so the link was a guaranteed 404. The React app
     * has no import-history page yet; the message now stands alone. */
    successMessage:
      'Your contacts have successfully started importing. Results will appear in the contacts list as they are processed.',

    /** Help link text */
    help: 'Help',
  },

  /**
   * Create contact dialog messages
   */
  createContact: {
    /** Dialog title */
    title: 'Create Contact',

    /** Section labels */
    contactMethodsLabel: 'Contact Methods',
    labelsLabel: 'Labels',
    avatarLabel: 'Avatar',

    /** Placeholder texts */
    addMethodPlaceholder: 'Add email or phone...',
    searchLabelsPlaceholder: 'Search or add labels...',

    /**
     * A new contact needs something to identify it by, but no single field is
     * mandatory — a name, an internal ID, or a contact method each satisfy it.
     */
    identifierHint: 'Enter at least one of: name, internal ID, or contact method.',

    /** Validation errors */
    nameTooLong: 'Name must be 75 characters or less',
    identifierRequired: 'Enter a name, internal ID, or contact method',

    /** Upload/submission error */
    uploadError: 'There was an error uploading. Please try again.',

    /** Indicator for filled sections */
    filled: '(filled)',
    selected: '(selected)',
  },

  /**
   * Share dialog extended messages
   */
  shareExtended: {
    /** Delivery-method option tooltips (icon-only SegmentedControl). The short
     * aria-label ("Email"/"SMS"/"App") names each option; this descriptive
     * tooltip (surfaced to screen readers via aria-describedby) explains it.
     * Shared by ShareDialog + BulkShareDialog. */
    delivery: {
      emailTip: 'Email — deliver the invitation to the contact’s email address',
      textTip: 'Text — deliver the invitation by SMS text message',
      appTip: 'App only — no email or text is sent; the invitation appears only in the app',
    },

    /** Editing scheduled invitation alert */
    editingScheduled: 'Editing scheduled invitation. Changes will update the scheduled send.',

    /** Message placeholder */
    messagePlaceholder: 'Add a message (optional)...',

    /** Recurring invitation tooltip */
    recurringTooltip: 'Set up recurring invitations',

    /** Schedule tooltip */
    scheduleTooltip: 'Schedule for later',

    /** Send now tooltip */
    sendNowTooltip: 'Send now instead',

    /** Resend every label */
    resendEvery: 'Resend every',

    /** Day/days labels */
    day: 'day',
    days: 'days',

    /** Error messages */
    updateFailed: 'Failed to update invitation.',
    sendFailed: 'Failed to send invitation.',

    /** Visible text before the sender name (staff only, #395 / vingapi#771; Q-506 10-14 card 1579) */
    fromLabel: 'From:',

    /** Accessible name of the sender trigger (the visible "From:" is plain text beside it) */
    fromAriaLabel: 'From',

    /** Suffix appended to the current user's name in the send-as picker */
    sendAsSelfSuffix: '(you)',
  },

  /**
   * Scheduled invitation dialog messages
   */
  scheduledInvitation: {
    /** Delete dialog title */
    deleteTitle: 'Delete Invitation',

    /** Delete confirmation text (non-recurring) */
    deleteConfirm: 'Are you sure you want to delete this invitation?',

    /** Delete recurring explanation */
    deleteRecurringText: 'This is a recurring invitation. You can delete just this occurrence or cancel the entire series.',

    /** Button label: delete single occurrence (preserves chain) */
    deleteThisOne: 'Delete This One',

    /** Button label: cancel entire recurring series */
    deleteEntireSeries: 'Delete Entire Series',

    /** Reschedule dialog title */
    rescheduleTitle: 'Reschedule Invitation',

    /** Reschedule dialog description */
    rescheduleDescription: 'Select a new date and time for this invitation.',
  },

  /**
   * Merge contacts extended messages
   */
  mergeExtended: {
    /** Success message */
    success: 'Contacts merged successfully.',

    /** Success - won't suggest again */
    successNoSuggest: 'This merge will not be suggested again.',

    /** Generic error */
    error: 'An error occurred while merging contacts. Please refresh and try again.',
  },

  /**
   * Merge suggestion dialog messages (auto-detected duplicate on contact detail)
   */
  /**
   * The `held_by_linked_contact` refusal (#223 / rule P1). vingapi stops a
   * create or an edit that would silently move an address off a contact in
   * this scope that is linked to a real account, and names the holder.
   *
   * The prompt names the person, not the rule. There is no override: the
   * escape hatch is the EXISTING remove-method action on the holder, which
   * the edit copy points at in words (no new button).
   */
  heldByLinkedContact: {
    /** Inside (edit and create): the heading names the address and its holder. */
    insideHeading: (address: string, holderName: string) =>
      `${address} is already on ${holderName}`,
    /** Inside: re-point the address (and its open work) onto this contact. */
    moveHere: (kind: 'email' | 'phone') =>
      kind === 'email' ? 'Move Email Here' : 'Move Phone Here',
    /** Inside, edit: merge the two contacts, keeping the holder. */
    mergeContacts: 'Merge Contacts',
    /** Inside, create: there is no contact yet — open the holder instead. */
    openContact: (holderName: string) => `Update ${holderName}`,
    /** #125: vingapi ran the merge inside the create/update and answered with
     *  the survivor — shown once we land on it. */
    mergedSuccess: 'Contacts merged.',

    /**
     * CROSSING (#234) — the address belongs to an account with a life outside
     * this scope. NOTHING about that account may be shown: no name, no card.
     */
    crossingHeading: (address: string) => `${address} is already in use`,
    crossingQuestion: (currentName: string) => `Is it ${currentName}'s?`,
    crossingSamePerson: 'Yes',
    crossingDifferentPerson: 'No',
  },

  /**
   * The `address_on_unmanaged_contact` refusal (#1813, vingapi#979). Owner
   * Q-587 a (words), Q-591 (frames). The title is built here from the
   * address, never from the server's `detail.message`.
   */
  unmanagedAddress: {
    title: (address: string) => `${address} is already on a contact you don't manage`,
    line: 'An admin can help. Take the address out to save without it.',
  },

  mergeSuggestion: {
    /** Dialog title */
    title: 'Possible Duplicate Found',

    /** Description text */
    description: 'These contacts may be the same person. Would you like to merge them?',

    /** Success message after merge */
    mergeSuccess: 'Contacts merged successfully.',

    /** Success message after ignore */
    ignoreSuccess: 'This merge will not be suggested again.',

    /** Generic error */
    error: 'An error occurred. Please try again.',
  },

  /**
   * SCORM import dialog messages
   */
  scormImport: {
    /** Dialog title */
    title: 'Import SCORM Package',

    /** Drop zone prompt */
    dropPrompt: 'Drop SCORM Package Here',

    /** Browse button */
    browse: 'Browse',

    /** Supported formats hint */
    supportedFormats: 'SCORM 1.2 and SCORM 2004 packages (ZIP format)',

    /** Invalid file type error */
    invalidFileType: 'Invalid file type. Please upload a SCORM package (ZIP file).',

    /** Upload in progress */
    uploading: 'Uploading...',

    /** Processing message */
    processing: 'Processing SCORM package...',

    /** Success title */
    successTitle: 'Import Complete',

    /** Success message */
    successMessage: (componentCount: number, questionCount: number) => {
      const parts = [];
      if (componentCount > 0) {
        parts.push(`${componentCount} component${componentCount !== 1 ? 's' : ''}`);
      }
      if (questionCount > 0) {
        parts.push(`${questionCount} question${questionCount !== 1 ? 's' : ''}`);
      }
      return parts.length > 0
        ? `Successfully imported ${parts.join(' and ')}.`
        : 'Import completed.';
    },

    /** No content imported */
    noContent: 'No importable content found in the package.',

    /** Generic error */
    error: 'Failed to import SCORM package. Please try again.',

    /** Warnings section title */
    warningsTitle: 'Warnings',

    /** Skipped items section title */
    skippedTitle: 'Skipped Items',

    /** View packet button */
    viewPacket: 'Open Packet',

    /** Import another button */
    importAnother: 'Import Another',
  },

  /**
   * Import assignments dialog (packet detail → status bar Import).
   * Legacy parity: vingclient dialog-upload-assignments.
   */
  importAssignments: {
    title: 'Import Assignments',
    /** Q-506 card 1544 (new): label of the share picker on a multi-share course. */
    addTo: 'Add to:',
    /** Q-506 card 1544 (reworded). */
    inactiveNotice:
      'Inactive on this share: only rows with Date Completed are imported.',
    columnsTitle: 'Expected columns',
    /**
     * Column names are the assignment export's header row, verbatim (vingapi
     * `AssignmentFields` / `ContactFields` headers; #1286, owner Q-348): a
     * manager who edits an export and re-imports it never types a second
     * vocabulary. Email and Phone are not export columns; they are the import's
     * own identifier aliases. Send Invitation is import-only (the refusal's spelling).
     */
    columnsHint: {
      lead: 'Use an assignment export as your file, or one row per person with these columns.',
      identify: 'Identify each person with: Email, Phone, Internal ID, or Contact ID.',
      optional:
        'Optional: Date Assigned, Date Due, Date Completed, Reassign Date, Expiration Date, Required (Yes/No), Unassigned (Yes/No), Send Invitation (Yes/No).',
      notes:
        'Archived is shown in exports for reference and is not imported. '
        + 'Rows with Date Completed are imported as completed history; other rows are invited unless Send Invitation is No. '
        + 'Unrecognized emails or phones create new contacts.',
    },
    /** Q-506 card 1544 (reworded). */
    dropPrompt: 'Drop a file here',
    supportedFormats: 'Supported formats: XLSX, CSV, TSV (max 10MB)',
    /** Q-506 card 1544 (reworded). */
    invalidFileType: 'Choose an XLSX, CSV, or TSV file.',
    /** Q-506 card 1544 (reworded). */
    fileTooLarge: 'This file is over 10MB.',
    /** Q-506 card 1544 (reworded). */
    singleFileOnly: 'Choose one file.',
    /** Q-506 card 1544 (reworded). */
    uploadFailed: 'Upload failed. Try again.',
    successTitle: 'Import started',
    /** Q-506 card 1544 (reworded). */
    successMessage: 'You’ll get a notification when it’s done.',
  },

  /**
   * Custom filter dialog messages
   */
  customFilter: {
    /** Dialog title templates */
    createTitle: 'Create Custom Contact Filter',
    updateTitle: 'Update Custom Contact Filter',

    /** Save instruction */
    saveAs: 'Save custom contact filter as:',

    /** Placeholder */
    namePlaceholder: 'Enter filter name',

    /** Preview heading + empty/rename notes */
    capturedHeading: 'This filter will include:',
    renameNote: 'Renaming keeps the captured filters unchanged.',

    /** Empty state message */
    noCriteria: 'No filters applied',

    /** Validation errors */
    nameRequired: 'Filter name is required',
    nameLengthError: 'Filter name must be between 3 and 40 characters',
    emptyFilter: 'Apply at least one filter before saving',
  },

  /**
   * Integration disconnect messages
   */
  syncError: {
    title: 'Sync Error Details',
    copied: 'Copied!',
    copy: 'Copy',
  },

  disconnect: {
    title: (name: string) => `Disconnect ${name}`,
    body: (name: string) =>
      `This will disconnect the ${name} integration. Historical data will be preserved, but syncing will stop. You can reconnect later through the ${name} Marketplace.`,
    confirm: 'Disconnect',
  },
  /**
   * Delete a saved filter that unsent sends still use (vingreact#1103, owner Q-410 a,
   * words 1-11). Every string is built here; the server's own sentence is never shown.
   */
  /** The footer confirm for deleting the selected saved filters (vingreact#1552, Q-577 1552-filters). No sentence. */
  savedFilterDelete: {
    one: (filterName: string) => `Delete "${filterName}"?`,
    many: (count: number) => `Delete ${count} ${pluralize('filter', count)}?`,
  },
  savedFilterInUse: {
    /** Word 1. */
    title: (filterName: string) => (filterName ? `Delete "${filterName}"?` : 'Delete this filter?'),
    /** Word 2. */
    lead: (sendCount: number) =>
      `${sendCount} ${sendCount === 1 ? 'send that hasn\u2019t' : 'sends that haven\u2019t'} gone out yet ${sendCount === 1 ? 'uses' : 'use'} this filter. Deleting it removes the filter from ${sendCount === 1 ? 'it' : 'them'}; the rest of each audience stays.`,
    /** Word 3, appended to the lead. */
    leadNoOne: (noOneCount: number, sendCount: number) =>
      sendCount === 1 ? 'It would then go to no one.' : `${noOneCount} of them would then go to no one.`,
    /** Word 4. */
    contentType: (type: string | null) =>
      type === 'checklist' ? 'Checklist' : type === 'packet' ? 'Course' : null,
    /** Word 5. */
    statusDraft: 'Draft \u00b7 not scheduled',
    statusScheduled: (date: string) => `Scheduled \u00b7 ${date}`,
    statusParked: (date: string) => `Parked \u00b7 ${date}`,
    recurrence: (days: number) => `every ${days} ${days === 1 ? 'day' : 'days'}`,
    /** Word 6. */
    recipients: (n: number) => `${n} ${n === 1 ? 'recipient' : 'recipients'}`,
    /** Word 7. */
    sendingNow: 'Sending now; it keeps this filter',
    /** Fallback title for a row whose content has no title. */
    untitled: 'Untitled',
    /** Word 8. */
    othersSends: (m: number) =>
      `${m} more ${m === 1 ? 'send' : 'sends'} by other people will also lose this filter.`,
    /** Q-428 F: the line when the admin has no rows of their own. */
    othersSendsOnly: (m: number) =>
      `${m} ${m === 1 ? 'send' : 'sends'} by other people ${m === 1 ? 'uses' : 'use'} this filter and will lose it.`,
    /** Word 9. */
    cancel: 'Cancel',
    confirm: (n: number) => `Delete filter and update ${n} ${n === 1 ? 'send' : 'sends'}`,
    confirmNothingToUpdate: 'Delete filter',
    /** Word 11. */
    failed: 'Something went wrong. Please try again.',
  },
} as const;

export type DialogMessageCategory = keyof typeof dialogMessages;
